import React, { useCallback, useEffect } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { equals, map, pluck } from "ramda";
import { getEmptyImage } from "react-dnd-html5-backend";
import DragItemTypes from "../DragItemTypes";
import {
  useAddToCollection,
  useRemoveContentFromCollection,
} from "../../collections/collectionServices";
import { useDeleteModuleItems } from "../../modules/moduleServices";
import {
  groupChildrenByParent,
  getContentParentsForAnthology,
} from "../organizeUtil";
import { getIds } from "../../../util/mediaUtil";
import { useParentDrop } from "../use-parentDrop";
import { useDrag } from "react-dnd";
import { notBlank } from "../../../util/ext";
import { useDnD, useSelected } from "../OrganizeProvider";

import SidebarTreeItemLabel from "./SidebarTreeItemLabel";
import { TreeItem } from "@mui/x-tree-view/TreeItem";

const useStyles = makeStyles(theme => ({
  content: {
    backgroundColor: "transparent",
  },

  contentHighlighted: {
    backgroundColor: `${theme.palette.primary.main} !important`,
  },

  header: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: "1.6rem",
  },

  iconContainer: {
    width: 0,
  },

  label: {
    color: "inherit",
  },

  labelHighlighted: {
    color: "white",
  },

  treeItem: {
    fontWeight: "inherit",
  },
}));

const SidebarUngroupedCollectionTreeItem = props => {
  const classes = useStyles();
  const {
    collection,
    count,
    handleMove,
    icon,
    onClick,
    onLabelClick,
    label,
    nestedTree,
    nodeId,
    nodeType,
    selected,
    showAlert,
    tooltipLabel,
    ...otherProps
  } = props;

  const dnd = useDnD();
  const { selectedContent, removeAllSelectedContent } = useSelected();

  const getTypographyProps = type => {
    switch (type) {
      case "root":
        return { classes: { root: classes.header } };
      default:
        return { classes: { root: classes.treeItem } };
    }
  };

  //-- Add Media to Collection
  const addToCollection = useAddToCollection();
  const deleteModuleItems = useDeleteModuleItems();
  const removeContentFromCollection = useRemoveContentFromCollection();
  const onDrop = useCallback(
    async ({ items, dropAction }) => {
      try {
        if (equals(dropAction, "move")) {
          const prevParents = groupChildrenByParent(items);

          // Remove children from previous parents
          for (const { prevParent, newChildren } of prevParents) {
            if (notBlank(prevParent.id)) {
              if (equals(prevParent.mediaType, "collection")) {
                await removeContentFromCollection.mutateAsync({
                  collectionId: prevParent.id,
                  contentIds: newChildren.ids,
                });
              }

              if (equals(prevParent.mediaType, "anthology")) {
                // Remove content from all collections in anthology
                const collections = getContentParentsForAnthology(
                  selectedContent,
                  prevParent,
                );

                await Promise.all(
                  map(collectionId =>
                    removeContentFromCollection.mutateAsync({
                      collectionId,
                      contentIds: getIds(selectedContent),
                    }),
                  ),
                  getIds(collections),
                );
                removeAllSelectedContent();
              }

              if (equals(prevParent.mediaType, "module")) {
                await deleteModuleItems.mutateAsync({
                  id: prevParent.id,
                  ids: newChildren.ids,
                });
              }
            }
          }
        }

        await addToCollection.mutateAsync({
          resourceIds: { id: collection.id },
          data: { ids: pluck("id", items) },
        });

        showAlert("success", ["Content Successfully Added"]);
      } catch (err) {
        showAlert("error", [err.message]);
      }
    },
    [
      addToCollection,
      collection,
      deleteModuleItems,
      selectedContent,
      showAlert,
      removeAllSelectedContent,
      removeContentFromCollection,
    ],
  );

  const [{ isOver, canDrop }, drop] = useParentDrop({
    parent: collection,
    onDrop,
    accept: [DragItemTypes.CONTENT],
  });

  // Bug prevented drag when values passed to dep array
  // https://github.com/react-dnd/react-dnd/issues/3479
  const [, drag, preview] = useDrag(
    () => ({
      type: DragItemTypes.COLLECTION,
      item: () => {
        dnd.updateDragAction("Move");
        return {
          id: collection.id,
          mediaType: "collection",
          onDropSuccess: handleMove,
        };
      },
      end: () => dnd.updateDragAction(""),
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [handleMove],
  );

  // Create Draggable & Droppable Element
  // https://github.com/react-dnd/react-dnd/issues/1451
  const dragDropRef = useCallback(
    node => {
      drag(node);
      drop(node);
    },
    [drag, drop],
  );

  // This is required for creating a custom drag overlay
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  return (
    <TreeItem
      ref={dragDropRef}
      itemId={nodeId}
      onClick={onLabelClick}
      classes={{
        iconContainer: classes.iconContainer,
        content:
          (isOver && canDrop) || selected
            ? classes.contentHighlighted
            : classes.content,
        label:
          (isOver && canDrop) || selected
            ? classes.labelHighlighted
            : classes.label,
      }}
      label={
        <SidebarTreeItemLabel
          item={{
            child: collection,
            parent: undefined,
            prevMediaList: undefined,
          }}
          tooltipLabel={tooltipLabel}
          labelText={label}
          icon={icon}
          TypographyProps={getTypographyProps(nodeType)}
        />
      }
      {...otherProps}
    >
      {nestedTree}
    </TreeItem>
  );
};

export default SidebarUngroupedCollectionTreeItem;
